import { useEffect, useMemo, useState } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useOverlay from '@/hooks/useOverlay'
import {
  useDeleteImporterEmail,
  useGetImporterEmails,
  useUpdateImporterEmail,
} from '@/service-library/hooks/importer-emails'
import { useGetProjects } from '@/service-library/hooks/projects'
import useAllOrganizations from '@/services/hooks/useAllOrganizations'
import { ImporterEmail } from '@/types/importer-emails'
import { showErrorSnackbar } from '@/utils/snackbars'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import DeleteImporterEmailDialog from '../project-settings/DeleteImporterEmailDialog'
import AddImporterEmailDialog from './AddImporterEmailDialog'

export default function UploadEmailAddresses() {
  const addEmailOverlay = useOverlay()
  const deleteEmailOverlay = useOverlay()

  const { isAuthorized: canEditProjects } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )

  const { projects } = useGetProjects({
    filters: { fields__only: 'id,name', limit: '1000' },
  })

  const {
    importerEmails = [],
    isPending,
    queryKey,
  } = useGetImporterEmails({
    filters: { fields__include: 'org', limit: '1000' },
  })

  const { organizations, isPending: isFetchingAllOrgs } = useAllOrganizations()

  const [importerEmail, setImporterEmail] = useState<ImporterEmail>()

  const { deleteImporterEmail } = useDeleteImporterEmail({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Unable to delete email. Please try again later.')
    },
  })

  const { updateImporterEmail } = useUpdateImporterEmail({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Unable to update email. Please try again later.')
    },
  })

  function handleOnDelete() {
    importerEmail && deleteImporterEmail(importerEmail.id)
    deleteEmailOverlay.close()
  }

  useEffect(() => {
    if (!addEmailOverlay.isOpen && !deleteEmailOverlay.isOpen && importerEmail)
      setImporterEmail(undefined)
  }, [addEmailOverlay.isOpen, deleteEmailOverlay.isOpen, importerEmail])

  const columns: MRT_ColumnDef<ImporterEmail>[] = useMemo(
    () => [
      {
        header: 'Organization',
        accessorFn: (row) => {
          const organization = organizations.find((o) => o.id === row.org_id)
          return `${organization?.name} ${
            !organization?.parent_org_id ? '(Root)' : ''
          }`
        },
      },
      {
        header: 'Project',
        accessorFn: (row) => {
          const project = projects.find((p) => p.id === row.project_id)
          return project ? project.name : 'Auto Assign'
        },
      },

      {
        accessorKey: 'append_email_body',
        header: 'Append Email Body',
        size: 200,
        enableSorting: false,
        Header: (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>Append Email Body</span>
            <Tooltip title="When on, the email body is added to the end of each attachment. Emails with no attachments always import the body.">
              <IconButton size="small" aria-label="About Append Email Body">
                <InfoOutlined fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
        Cell: ({ cell, row }) => (
          <Switch
            size="small"
            checked={cell.getValue<boolean>()}
            disabled={!canEditProjects}
            onChange={(event) => {
              updateImporterEmail({
                ...row.original,
                append_email_body: event.target.checked,
              })
            }}
            slotProps={{ input: { 'aria-label': 'Append Email Body' } }}
          />
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 400,
        enableClickToCopy: true,
      },
    ],
    [organizations, projects, canEditProjects, updateImporterEmail],
  )

  const table = useZerapixTable<ImporterEmail>({
    title: (
      <Stack>
        <Typography variant="h6">Upload Email Addresses</Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ maxWidth: 700 }}
        >
          Forward emails to these addresses to process them as documents. Each
          attachment is uploaded as a separate document. Turn on Append Email
          Body to add the email body to each attachment. Emails with no
          attachments are processed using the body.
        </Typography>
      </Stack>
    ),
    columns,
    data: importerEmails,
    initialState: {
      density: 'comfortable',
    },
    enableColumnResizing: true,
    enableRowActions: true,
    enableSorting: true,
    localization: {
      noRecordsToDisplay: 'No emails have been added.',
    },
    muiTableBodyRowProps: {
      hover: false,
    },
    renderRowActionMenuItems: canEditProjects
      ? ({ closeMenu, row }) => {
          return [
            <MenuItem
              key="delete"
              onClick={() => {
                deleteEmailOverlay.open()
                setImporterEmail(row.original)
                closeMenu()
              }}
            >
              <ListItemIcon>
                <Delete color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: { variant: 'inherit', color: 'error' },
                }}
              >
                Delete
              </ListItemText>
            </MenuItem>,
          ]
        }
      : undefined,
    renderTableActions: canEditProjects
      ? () => (
          <div style={{ flexShrink: 0 }}>
            <Button
              disabled={isFetchingAllOrgs}
              onClick={addEmailOverlay.open}
              startIcon={<Add />}
              variant="text"
            >
              New Email
            </Button>
          </div>
        )
      : undefined,
    state: {
      showSkeletons: isPending,
    },
  })

  return (
    <>
      <ZerapixTable<ImporterEmail> table={table} />
      <AddImporterEmailDialog
        isFetchingOrgs={isFetchingAllOrgs}
        listQueryKey={queryKey}
        organizations={organizations}
        projects={projects}
        overlay={addEmailOverlay}
        importerEmails={importerEmails}
      />
      {importerEmail && (
        <DeleteImporterEmailDialog
          importerEmail={importerEmail}
          overlay={deleteEmailOverlay}
          onDelete={handleOnDelete}
        />
      )}
    </>
  )
}
